import Seo from "../../Seo";
import { SITE_NAME, SITE_ORIGIN } from "../../../config/site";
import { LEGAL_OPERATOR } from "../../../constants/legalDocuments";
import { LANDING_PLANS_FALLBACK } from "../../../constants/planMarketing";
import { TRIAL_DAYS } from "../../../constants/trial";
import { IMG, isAudienceMenuPath, PATHS } from "./nav";
import { NICHE_PAGES } from "./nicheContent";

/**
 * Новый сайт на корневых адресах. true = index/follow для публичных страниц.
 * Ниши вне меню «Для кого» всегда noindex.
 */
export const NEW_SITE_LIVE = true;

const rub = (n) => new Intl.NumberFormat("ru-RU").format(n);
const PRICES = LANDING_PLANS_FALLBACK.map((p) => p.monthly_price_rub);
const PRICE_FROM = Math.min(...PRICES);
const PRICE_TO = Math.max(...PRICES);
const TRIAL = `${TRIAL_DAYS} дней бесплатно`;
const FROM = `от ${rub(PRICE_FROM)} ₽/мес`;
const CTA = `${TRIAL}, без карты, ${FROM}`;

const OG = (name) => `${SITE_ORIGIN}/og/${name}.jpg`;
const url = (path) => `${SITE_ORIGIN}${path || "/"}`;

const HOME_CRUMB = ["Главная", PATHS.home];
const AUDIENCE_CRUMB = ["Для кого", PATHS.audience];

const PAGES = {
  home: {
    path: PATHS.home,
    title: "CRM для спортивного клуба: журнал, абонементы, расписание",
    description:
      `${SITE_NAME} — CRM для спортивных клубов, секций и студий. Журнал посещений, абонементы, ` +
      `оплаты и приложение для клиентов в одном кабинете. ${CTA}.`,
    keywords: [
      "crm для спортивного клуба",
      "программа для спортивной секции",
      "crm для фитнес клуба",
      "журнал посещаемости",
      "учёт абонементов",
      "расписание тренировок",
      "приложение для спортивного клуба",
    ],
    og: "default",
    crumbs: [],
  },
  features: {
    path: PATHS.features,
    title: "Возможности CRM для спортклуба: кабинеты, оплаты, соцсеть",
    description:
      `Расписание и журнал, абонементы, ЮKassa и Т-Банк, кабинеты клиента и тренера, ` +
      `соцсеть клуба и приложение под вашим брендом. Что входит в тарифы ${SITE_NAME}.`,
    keywords: [
      "возможности crm для спортивного клуба",
      "кабинет клиента спортивного клуба",
      "кабинет тренера",
      "онлайн-оплата абонементов",
      "приложение клуба под своим брендом",
    ],
    og: "default",
    crumbs: [HOME_CRUMB, ["Возможности", PATHS.features]],
  },
  pricing: {
    path: PATHS.pricing,
    title: `Тарифы CRM для спортивного клуба — ${FROM}`,
    description:
      `Тарифы ${SITE_NAME}: ${LANDING_PLANS_FALLBACK.map((p) => `«${p.name}» ${rub(p.monthly_price_rub)} ₽`).join(", ")} ` +
      `в месяц за весь клуб, без доплат за тренеров. Модули и калькулятор. ${CTA}.`,
    keywords: [
      "стоимость crm для спортивного клуба",
      "тарифы crm фитнес клуб",
      "цена программы для спортивной секции",
      `${SITE_NAME} тарифы`,
    ],
    og: "tarify",
    crumbs: [HOME_CRUMB, ["Тарифы", PATHS.pricing]],
    offers: true,
  },
  audience: {
    path: PATHS.audience,
    title: "CRM для клубов, студий, школ и тренеров",
    description:
      `Подходит клубам, студиям, школам, секциям и персональным тренерам — любой вид спорта. ` +
      `Журнал, абонементы и приложение клиентов. ${CTA}.`,
    keywords: [
      "crm для спортивной школы",
      "программа для детской секции",
      "crm для фитнес студии",
      "crm для персонального тренера",
    ],
    og: "default",
    crumbs: [HOME_CRUMB, AUDIENCE_CRUMB],
  },
  connect: {
    path: PATHS.connect,
    title: "Как подключить CRM для спортивного клуба",
    description:
      `Запуск ${SITE_NAME} за вечер: регистрация, расписание, перенос учеников из Excel, ` +
      `приглашение тренеров и клиентов, онлайн-оплата. Без внедрения. ${CTA}.`,
    keywords: [
      "подключить crm для клуба",
      "перенести базу клиентов из excel",
      "внедрение crm спортивный клуб",
    ],
    og: "default",
    crumbs: [HOME_CRUMB, ["Подключение", PATHS.connect]],
  },
  about: {
    path: PATHS.about,
    type: "AboutPage",
    title: `О компании ${SITE_NAME} — контакты и реквизиты`,
    description:
      `${SITE_NAME} — облачный сервис для спортивных клубов, секций и студий. ` +
      `Контакты поддержки и реквизиты: ${LEGAL_OPERATOR.legalName}, ИНН ${LEGAL_OPERATOR.inn}.`,
    keywords: [`${SITE_NAME} о компании`, `${SITE_NAME} реквизиты`, `${SITE_NAME} контакты`],
    og: "default",
    crumbs: [HOME_CRUMB, ["О компании", PATHS.about]],
  },
};

/** title без бренда (Seo добавит « — Бай.Спорт»), lead → description, свои keywords. */
const NICHE_SEO = {
  edinoborstva: {
    title: "CRM для школы единоборств: карате, дзюдо, бокс, ММА",
    lead:
      "Группы по возрасту, журнал на татами, абонементы и долги, приложение для родителей",
    keywords: [
      "crm для школы единоборств",
      "программа для клуба карате",
      "crm для секции бокса",
      "программа для школы дзюдо",
      "учёт учеников единоборства",
    ],
  },
  tancy: {
    title: "CRM для танцевальной школы и студии танцев",
    lead:
      "Направления и залы, журнал занятий, абонементы и разовые визиты, кабинет ученика",
    keywords: [
      "crm для школы танцев",
      "программа для танцевальной студии",
      "учёт учеников танцевальной школы",
      "абонементы студии танцев",
    ],
  },
  gimnastika: {
    title: "CRM для секции гимнастики и акробатики",
    lead: "Группы по уровням, журнал тренировок, абонементы и связь с родителями",
    keywords: [
      "crm для гимнастики",
      "программа для секции художественной гимнастики",
      "программа для школы акробатики",
      "учёт посещений гимнастика",
    ],
  },
  plavanie: {
    title: "CRM для школы плавания и бассейна",
    lead:
      "Детские и взрослые группы, журнал у бортика, абонементы, переносы и push родителям",
    keywords: [
      "crm для школы плавания",
      "программа для бассейна",
      "учёт абонементов бассейн",
      "журнал посещений плавание",
    ],
  },
  futbol: {
    title: "CRM для футбольной школы и секции",
    lead:
      "Группы по годам рождения, журнал тренировок, абонементы и взносы, кабинет родителей",
    keywords: [
      "crm для футбольной школы",
      "программа для футбольной секции",
      "учёт взносов футбольная школа",
      "программа для дюсш",
    ],
  },
  fitnes: {
    title: "CRM для фитнес-клуба: группы, персональные, абонементы",
    lead:
      "Групповые и персональные, залы и тренеры, журнал посещений и абонементы в одном сервисе",
    keywords: [
      "crm для фитнес клуба",
      "программа для фитнес студии",
      "учёт абонементов фитнес",
      "расписание групповых тренировок",
    ],
    og: "default",
  },
  yoga: {
    title: "CRM для студии йоги и пилатеса",
    lead:
      "Расписание классов, абонементы, заморозка и разовые визиты, приложение для учеников",
    keywords: [
      "crm для студии йоги",
      "программа для пилатеса",
      "абонементы йога студия",
      "учёт занятий йога",
    ],
    og: "default",
  },
  deti: {
    title: "CRM для детской спортивной секции",
    lead:
      "Группы по возрасту, журнал, абонементы и приложение, где родители видят посещения",
    keywords: [
      "crm для детской секции",
      "программа для детской спортивной школы",
      "приложение для родителей секция",
      "учёт посещений детей",
    ],
    og: "default",
  },
  rastyazhka: {
    title: "CRM для студии растяжки и стретчинга",
    lead:
      "Группы и персональные, абонементы и разовые визиты, журнал и остаток в приложении",
    keywords: [
      "crm для студии растяжки",
      "программа для студии стретчинга",
      "абонементы растяжка",
      "учёт занятий стретчинг",
    ],
    og: "default",
  },
  saykl: {
    title: "CRM для сайкл-студии: расписание и явка",
    lead:
      "Классы по расписанию, отметка «приду», абонементы и списание после занятия",
    keywords: [
      "crm для сайкл студии",
      "программа для cycle студии",
      "абонементы сайкл",
      "расписание сайкл классов",
    ],
    og: "default",
  },
  shkoly: {
    title: "CRM для спортивной школы и ДЮСШ",
    lead:
      "Несколько направлений, группы, тренеры, журнал и абонементы в одном учёте",
    keywords: [
      "crm для спортивной школы",
      "программа для дюсш",
      "учёт групп спортивная школа",
      "журнал спортивной школы",
    ],
    og: "default",
  },
  seti: {
    title: "CRM для сети спортивных клубов и филиалов",
    lead:
      "Филиалы со своими расписаниями и тренерами, общий учёт для владельца сети",
    keywords: [
      "crm для сети фитнес клубов",
      "программа для сети спортивных клубов",
      "учёт филиалов спорт",
      "crm несколько филиалов",
    ],
    og: "default",
  },
};

Object.entries(NICHE_SEO).forEach(([id, n]) => {
  const page = NICHE_PAGES[id];
  PAGES[id] = {
    path: page.path,
    title: n.title,
    description: `${n.lead}. ${SITE_NAME}: ${CTA}.`,
    keywords: n.keywords,
    og: n.og || id,
    crumbs: [HOME_CRUMB, AUDIENCE_CRUMB, [page.navTitle, page.path]],
  };
});

export const SEO_PAGES = PAGES;

const ORG_ID = `${SITE_ORIGIN}/#organization`;
const SITE_ID = `${SITE_ORIGIN}/#website`;
const APP_ID = `${SITE_ORIGIN}/#software`;

function buildGraph(page, faq) {
  const pageUrl = url(page.path);
  const graph = [
    {
      "@type": "Organization",
      "@id": ORG_ID,
      name: SITE_NAME,
      alternateName: ["Бай Спорт", "BaySport", "Bay Sport"],
      url: url(PATHS.home),
      logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}${IMG.logo}` },
      email: LEGAL_OPERATOR.supportEmail,
      legalName: LEGAL_OPERATOR.legalName,
      taxID: LEGAL_OPERATOR.inn,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: LEGAL_OPERATOR.supportEmail,
        availableLanguage: "ru",
      },
    },
    {
      "@type": "WebSite",
      "@id": SITE_ID,
      url: url(PATHS.home),
      name: SITE_NAME,
      inLanguage: "ru-RU",
      publisher: { "@id": ORG_ID },
    },
    {
      "@type": "SoftwareApplication",
      "@id": APP_ID,
      name: SITE_NAME,
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "CRM для спортивных клубов",
      operatingSystem: "Web, iOS, Android",
      url: url(PATHS.home),
      image: OG("default"),
      description: PAGES.home.description,
      publisher: { "@id": ORG_ID },
      offers: page.offers
        ? LANDING_PLANS_FALLBACK.map((p) => ({
            "@type": "Offer",
            name: `Тариф «${p.name}»`,
            description: p.description,
            price: String(p.monthly_price_rub),
            priceCurrency: "RUB",
            url: `${url(PATHS.pricing)}#plan-${p.code}`,
            availability: "https://schema.org/InStock",
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: String(p.monthly_price_rub),
              priceCurrency: "RUB",
              unitText: "месяц",
              billingDuration: "P1M",
            },
          }))
        : {
            "@type": "AggregateOffer",
            lowPrice: String(PRICE_FROM),
            highPrice: String(PRICE_TO),
            offerCount: String(PRICES.length),
            priceCurrency: "RUB",
            url: url(PATHS.pricing),
          },
    },
    {
      "@type": page.type || "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: page.title.includes(SITE_NAME) ? page.title : `${page.title} — ${SITE_NAME}`,
      description: page.description,
      inLanguage: "ru-RU",
      isPartOf: { "@id": SITE_ID },
      about: { "@id": APP_ID },
      primaryImageOfPage: { "@type": "ImageObject", url: OG(page.og) },
      ...(page.crumbs.length ? { breadcrumb: { "@id": `${pageUrl}#breadcrumb` } } : {}),
    },
  ];

  if (page.crumbs.length) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: page.crumbs.map(([name, path], i) => ({
        "@type": "ListItem",
        position: i + 1,
        name,
        item: url(path),
      })),
    });
  }

  if (faq?.length) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      url: pageUrl,
      mainEntity: faq.map(([q, a]) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

/** @param {{ page: keyof SEO_PAGES, faq?: [string, string][] }} props */
export function PageSeo({ page: id, faq }) {
  const page = PAGES[id];
  const niche = NICHE_PAGES[id];
  const hideFromIndex =
    !NEW_SITE_LIVE || (niche && !isAudienceMenuPath(niche.path));
  return (
    <Seo
      title={page.title}
      description={page.description}
      keywords={page.keywords}
      canonicalUrl={url(page.path === "/" ? "/" : page.path)}
      robots={hideFromIndex ? "noindex, follow" : undefined}
      ogImage={OG(page.og)}
      ogImageAlt={page.title}
      ogImageSize={[1200, 630]}
      jsonLd={buildGraph(page, faq)}
    />
  );
}
